import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import webservice from '../../../services/webservice';
import FeatureStats from './FeatureStats';
import FeatureFilter from './FeatureFilter';
import FeatureList from './FeatureList';
import { ComponentLoader } from '../common/Loader';
import '../APIAnalytics/UserAPIAnalytics/AuditLog.css'; // Leverage existing dashboard styles

const FeatureIndex = () => {
    // List State
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // Stats State
    const [statsData, setStatsData] = useState(null);
    const [statsLoading, setStatsLoading] = useState(false);

    // Filter & Pagination State
    const [filters, setFilters] = useState({
        search: '',
        feature: '',
        status: '',
        startDate: '',
        endDate: ''
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const fetchStats = async (currentFilters) => {
        setStatsLoading(true);
        try {
            const response = await axios.post(`${webservice}api/microservice-feature/stats`, {
                params: currentFilters
            });
            if (response.data.success) {
                setStatsData(response.data.data);
            }
        } catch (err) {
            console.error('Failed to fetch stats', err);
        } finally {
            setStatsLoading(false);
        }
    };

    const fetchList = async (currentFilters, page, limit) => {
        setLoading(true);
        setError(null);
        try {
            const payload = {
                ...currentFilters,
                page,
                limit
            };
            const response = await axios.post(`${webservice}api/microservice-feature/list`, {
                params: payload
            });
            if (response.data.success) {
                setData(response.data.data);
                setTotalRecords(response.data.totalRecords);
                setTotalPages(response.data.totalPages);
            } else {
                setError(response.data.message || 'Failed to fetch feature list.');
            }
        } catch (err) {
            console.error('Failed to fetch list', err);
            setError('Error loading microservice features. Please try again.');
            setData([]);
            setTotalRecords(0);
            setTotalPages(0);
        } finally {
            setLoading(false);
        }
    };

    // Refetch when filters or pagination change
    useEffect(() => {
        fetchStats(filters);
    }, [filters]);

    useEffect(() => {
        fetchList(filters, currentPage, itemsPerPage);
    }, [filters, currentPage, itemsPerPage]);

    const handleFilterChange = useCallback((newFilters) => {
        setFilters(newFilters);
        setCurrentPage(1); // Reset to first page
    }, []);

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    const handleItemsPerPageChange = (newLimit) => {
        setItemsPerPage(newLimit);
        setCurrentPage(1);
    };

    return (
        <div className="audit-log-container">
            <div className="audit-log-header">
                <h2>Microservice Features Analytics</h2>
            </div>

            <FeatureStats data={statsData} loading={statsLoading} />

            <FeatureFilter filters={filters} onFilterChange={handleFilterChange} />

            {error && <div className="text-danger mb-4">{error}</div>}

            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
                    <ComponentLoader size="50px" />
                </div>
            ) : (
                <FeatureList
                    data={data}
                    totalRecords={totalRecords}
                    totalPages={totalPages}
                    currentPage={currentPage}
                    itemsPerPage={itemsPerPage}
                    onPageChange={handlePageChange}
                    onItemsPerPageChange={handleItemsPerPageChange}
                />
            )}
        </div>
    );
};

export default FeatureIndex;
